"use client";

import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { getOrCreateVisitorId } from "@/utils/helpers/storageUtils";
import { sendAnalytics } from "@/utils/helpers/analytics";
import "./HindiEvPromoLanding.styles.scss";

function readUrlParamsForLead() {
  try {
    const raw = localStorage.getItem("urlParams");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Digits only, country code, no + (e.g. 918047484881). Set NEXT_PUBLIC_WHATSAPP_WA_NUMBER in env. */
const POST_SOURCE = "innovolt-hindi-promo";
const POST_ACTION = "openContact";
const POST_ACTION_VIDEO = "openVideo";
const SHORTS_EMBED_URL =
  "https://www.youtube.com/embed/csXbIM2cRTc?autoplay=1&rel=0&modestbranding=1&playsinline=1";
const ANALYTICS_LANG = "hi";

const HindiEvPromoLanding = () => {
  const [contactOpen, setContactOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [msg, setMsg] = useState({ text: "", ok: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data;
      if (d?.source === POST_SOURCE && d?.action === POST_ACTION) {
        sendAnalytics(
          "hindi_promo_button_click",
          { button_name: "contact_cta", source: "iframe" },
          ANALYTICS_LANG
        );
        setMsg({ text: "", ok: false });
        setContactOpen(true);
      }
      if (d?.source === POST_SOURCE && d?.action === POST_ACTION_VIDEO) {
        sendAnalytics(
          "hindi_promo_button_click",
          { button_name: "video_thumbnail", source: "iframe" },
          ANALYTICS_LANG
        );
        setVideoOpen(true);
      }
      if (d?.source === POST_SOURCE && d?.action === "foldView" && d?.fold) {
        sendAnalytics(
          "hindi_promo_fold_view",
          { fold_name: d.fold, source: "iframe" },
          ANALYTICS_LANG
        );
      }
      if (d?.source === POST_SOURCE && d?.action === "buttonClick" && d?.button) {
        sendAnalytics(
          "hindi_promo_button_click",
          { button_name: d.button, source: "iframe" },
          ANALYTICS_LANG
        );
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const timer = window.setTimeout(() => {
      setMsg({ text: "", ok: false });
      setContactOpen(true);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    document.body.style.overflow = contactOpen || videoOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [contactOpen, videoOpen, mounted]);

  const closeModal = useCallback(() => {
    setContactOpen(false);
    setMsg({ text: "", ok: false });
  }, []);

  useEffect(() => {
    if (!contactOpen && !videoOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (contactOpen) closeModal();
        if (videoOpen) setVideoOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [contactOpen, videoOpen, closeModal]);

  const closeVideo = useCallback(() => {
    setVideoOpen(false);
  }, []);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setMsg({ text: "", ok: false });
      const formEl = e.currentTarget;
      const fd = new FormData(e.currentTarget);
      const name = String(fd.get("name") || "").trim();
      const phone = String(fd.get("phone") || "").replace(/\D/g, "");
      const city = String(fd.get("city") || "").trim();
      const geoState = city === "Delhi NCR" ? "Delhi" : "Uttar Pradesh";
      if (phone.length !== 10) {
        setMsg({
          text: "कृपया वैध 10 अंकों का मोबाइल नंबर दर्ज करें।",
          ok: false,
        });
        return;
      }
      setSubmitting(true);
      sendAnalytics(
        "hindi_promo_button_click",
        { button_name: "contact_submit", source: "react_modal" },
        ANALYTICS_LANG
      );
      try {
        const paraLocal = readUrlParamsForLead();
        const userDetails = {
          name: name || null,
          phone: "+91" + phone,
          createdBy: "innovolt-website",
          language: "hi",
          ...(city ? { geoCity: city, geoState } : {}),
          metaDataList: [
            { key: "UTM_SOURCE", value: paraLocal?.utm_source ?? "Organic" },
            { key: "UTM_CAMPAIGN", value: paraLocal?.utm_campaign ?? "999" },
            { key: "UTM_MEDIUM", value: paraLocal?.utm_medium ?? "Organic" },
            { key: "UTM_CONTENT", value: paraLocal?.utm_content ?? "Organic" },
            { key: "OFFER_SUB_SOURCE", value: "hindi-ev-promo" },
            { key: "SOURCE_USER_ID", value: getOrCreateVisitorId() || "unknown" },
            { key: "SOURCE_NAME", value: "innovolt-website" },
            { key: "UTM_TERM", value: paraLocal?.utm_term ?? "Unknown" },
            { key: "UTM_ENTIRE_URL", value: paraLocal?.entire_url ?? "" },
            { key: "WEBSITE_LANGUAGE", value: "hi" },
          ],
        };

        let res;
        try {
          res = await fetch("/api/hindi-promo-lead", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(userDetails),
          });
        } catch {
          sendAnalytics(
            "hindi_promo_lead_submit_failed",
            { source: "react_modal", reason: "network_error" },
            ANALYTICS_LANG
          );
          setMsg({
            text: "नेटवर्क त्रुटि। कृपया पुनः प्रयास करें।",
            ok: false,
          });
          return;
        }

        let data = {};
        try {
          data = await res.json();
        } catch {
          data = {};
        }

        if (res.ok) {
          sendAnalytics(
            "hindi_promo_lead_submit_success",
            { source: "react_modal" },
            ANALYTICS_LANG
          );
          setMsg({
            text: "धन्यवाद! हम जल्द ही आपसे संपर्क करेंगे।",
            ok: true,
          });
          try {
            formEl?.reset?.();
          } catch {
            /* ignore */
          }
          window.setTimeout(closeModal, 2200);
        } else {
          sendAnalytics(
            "hindi_promo_lead_submit_failed",
            { source: "react_modal", status: res.status },
            ANALYTICS_LANG
          );
          setMsg({
            text:
              data.message ||
              "कुछ गलत हो गया। कृपया बाद में पुनः प्रयास करें।",
            ok: false,
          });
        }
      } catch (err) {
        // This catch is for unexpected client-side errors (not network).
        // Keep message user-friendly; details can be inspected in console.
        // eslint-disable-next-line no-console
        console.error("Hindi promo lead submit failed:", err);
        sendAnalytics(
          "hindi_promo_lead_submit_failed",
          { source: "react_modal", reason: "client_exception" },
          ANALYTICS_LANG
        );
        setMsg({
          text: "कुछ गलत हो गया। कृपया बाद में पुनः प्रयास करें।",
          ok: false,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [closeModal]
  );

  const openContactFromWhatsAppFab = useCallback(() => {
    sendAnalytics(
      "hindi_promo_button_click",
      { button_name: "whatsapp_fab", source: "react" },
      ANALYTICS_LANG
    );
    setMsg({ text: "", ok: false });
    setContactOpen(true);
  }, []);

  const contactModal =
    mounted &&
    contactOpen &&
    createPortal(
      <div
        className="hindiEvPromoContactModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hindiEvPromoContactTitle"
      >
        <button
          type="button"
          className="hindiEvPromoContactModal__backdrop"
          aria-label="बंद करें"
          onClick={closeModal}
        />
        <div className="hindiEvPromoContactModal__panel">
          <div className="hindiEvPromoContactModal__header">
            <h2 id="hindiEvPromoContactTitle">हमसे संपर्क करें</h2>
            <button
              type="button"
              className="hindiEvPromoContactModal__close"
              aria-label="बंद करें"
              onClick={closeModal}
            >
              &times;
            </button>
          </div>
          <form className="hindiEvPromoContactModal__form" onSubmit={onSubmit}>
            <p
              className={`hindiEvPromoContactModal__msg${msg.ok ? " hindiEvPromoContactModal__msg--ok" : ""}`}
              role="alert"
            >
              {msg.text}
            </p>
            <label>
              नाम
              <input name="name" type="text" autoComplete="name" />
            </label>
            <label>
              मोबाइल नंबर
              <input
                name="phone"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                autoComplete="tel"
              />
            </label>
            <label>
              शहर
              <select name="city" defaultValue="" autoComplete="address-level2">
                <option value="" disabled>
                  शहर चुनें
                </option>
                <option value="Agra">Agra</option>
                <option value="Delhi NCR">Delhi NCR</option>
                <option value="Kanpur">Kanpur</option>
                <option value="Lucknow">Lucknow</option>
              </select>
            </label>
            <button
              type="submit"
              className="hindiEvPromoContactModal__submit"
              disabled={submitting}
            >
              संपर्क करें
            </button>
          </form>
        </div>
      </div>,
      document.body
    );

  const videoModal =
    mounted &&
    videoOpen &&
    createPortal(
      <div
        className="hindiEvPromoVideoModal"
        role="dialog"
        aria-modal="true"
        aria-label="वीडियो"
      >
        <button
          type="button"
          className="hindiEvPromoVideoModal__backdrop"
          aria-label="बंद करें"
          onClick={closeVideo}
        />
        <div className="hindiEvPromoVideoModal__panel">
          <div className="hindiEvPromoVideoModal__header">
            <h2>वीडियो</h2>
            <button
              type="button"
              className="hindiEvPromoVideoModal__close"
              aria-label="बंद करें"
              onClick={closeVideo}
            >
              &times;
            </button>
          </div>
          <iframe
            className="hindiEvPromoVideoModal__frame"
            src={SHORTS_EMBED_URL}
            title="YouTube Shorts"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>,
      document.body
    );

  return (
    <div className="hindiEvPromoLanding">
      {contactModal}
      {videoModal}
      <button
        type="button"
        className="hindiEvPromoLanding__waFab"
        onClick={openContactFromWhatsAppFab}
        aria-label="WhatsApp — फॉर्म भरें"
      >
        <Image
          src="/images/whatsapp-icon.png"
          alt=""
          width={56}
          height={56}
          className="hindiEvPromoLanding__waFabIcon"
        />
      </button>
      <div className="hindiEvPromoLanding__cqHost">
        <div className="hindiEvPromoLanding__viewport">
          <div className="hindiEvPromoLanding__inner">
            <iframe
              className="hindiEvPromoLandingIframe"
              src="/hindi-ev-promo.html"
              title="Innovolt — EV loan and brands (Hindi)"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HindiEvPromoLanding;
